import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { Loader2, ArrowLeft, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTest } from "@/context/TestContext";
import { useAuth } from "@/context/AuthContext";
import { useDevMode } from "@/context/DevModeContext";
import { buildSampleTestPayload } from "@/utils/sampleTest";
import { generationConfig as canonicalGenerationConfig, type Question, type Blueprint, type TestConfig } from "@shared/schema";
import { normalizeAnswerCapitalization } from "@/utils/sortAnswers";
import { HttpError, requestJson, authHeaders } from "@/lib/generationRequests";
import {
  describeDuration,
  finalizingBudgetSeconds,
  NO_PROGRESS_NOTICE_SECONDS,
  refinedGeneratingCeilingSeconds,
} from "@shared/generationTiming";

// Live-progress generation flow (2026-07-06): submit, then poll real BCE
// status (n/m tasks, CDE dedup running, etc.) instead of guessing at a static
// "should take about N minutes" estimate with no relation to what's actually
// happening. See server/routes.ts /api/generate-questions/{start,status,finish}.

// HttpError / requestJson / authHeaders moved to @/lib/generationRequests
// (ARC-30) so "Add item" shares one implementation of the bounded-request
// discipline rather than growing a weaker second copy. The reasoning that
// produced them lives with them.

// Every generation call gets a hard ceiling — see requestJson().
const START_TIMEOUT_MS = 60_000;
const STATUS_TIMEOUT_MS = 15_000;
const FINISH_TIMEOUT_MS = 90_000;

/**
 * What this deployment has actually recorded for tests of this size, returned
 * by /start alongside the session. Absent (or `sampleCount` 0) on a fresh
 * install, on MemStorage, and whenever the stats query fails — all of which
 * fall back to the observation-derived constants in shared/generationTiming.
 */
type GenerationEstimate = {
  estimateSeconds: number | null;
  sampleCount: number;
  source: "refined" | "base" | "fallback";
  windowDays: 7 | 30 | null;
};

async function startGeneration(
  blueprint: Blueprint,
  config?: TestConfig,
  testName?: string,
  token?: string | null
): Promise<{
  sessionId: string;
  totalTasks: number;
  generationSessionId?: string;
  estimate?: GenerationEstimate;
}> {
  return requestJson("/api/generate-questions/start", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ blueprint, config: config ? canonicalGenerationConfig(config) : undefined, testName }),
  }, START_TIMEOUT_MS, "Starting generation");
}

export type GenerationStatus = {
  status: string;
  message: string;
  completedTasks: number;
  totalTasks: number;
};

async function checkGenerationStatus(sessionId: string, token?: string | null): Promise<GenerationStatus> {
  return requestJson(`/api/generate-questions/status/${sessionId}`, {
    headers: authHeaders(token),
  }, STATUS_TIMEOUT_MS, "Status check");
}

async function finishGeneration(
  sessionId: string,
  blueprint: Blueprint,
  config: TestConfig | undefined,
  generationSessionId: string | undefined,
  token?: string | null
): Promise<Question[]> {
  const result = await requestJson("/api/generate-questions/finish", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ sessionId, blueprint, config: config ? canonicalGenerationConfig(config) : undefined, generationSessionId }),
  }, FINISH_TIMEOUT_MS, "Retrieving your questions");
  return result.questions;
}

// ---------------------------------------------------------------------------
// Hang-prevention budget (ARC-26, 2026-08-13)
//
// Every constant here exists to bound something that was previously
// unbounded. Written deliberately without any information from BCE: we do not
// enumerate what BCE can do, we bound how long we are willing to wait for it
// and make every unknown state loud and named.
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3000;
// Show a visible "having trouble reaching the server" line after this many
// consecutive failed polls (~9s) instead of a silent console.warn.
const POLL_TROUBLE_AFTER = 3;
// Give up on a permanently broken poll well before the watchdog would (~2min
// of continuous failure), so the user gets a real error rather than a healthy
// looking spinner for the rest of the deadline.
const MAX_CONSECUTIVE_POLL_FAILURES = 40;
// How long an unrecognised session status is tolerated before we treat it as
// terminal. Not zero: an unknown value could equally be an early in-progress
// state we have never seen ("queued", "pending"), and killing every healthy
// generation would be a far worse failure than waiting one more minute.
const UNKNOWN_STATUS_GRACE_MS = 60_000;
// The finalizing phase (all tasks reported complete, BCE still working — CDE
// dedup / re-competition) gets its OWN budget rather than sharing the
// per-task one. This phase does not shrink with test size: blacksmith.ts
// records a measured 990s for a 20-task batch, and the field report on ARC-26
// describes 10-15 minutes on a small test. Folded into a per-task multiplier
// it meant small tests could spend their entire budget here — a 3-item test
// had 11 minutes total. Inverted intuition, real bug.
//
// ARC-30 moved the number itself into shared/generationTiming.ts, where it
// cites the observation it comes from (20 min -> 30 min, 2x the upper end of
// the observed 10-15 minute range).
const FINALIZING_BUDGET_MS = finalizingBudgetSeconds() * 1000;
// How long the run may report no forward movement before the UI SAYS SO. Not a
// deadline: nothing fails when it passes. A stalled-looking spinner is a
// reporting problem, and the fix for a reporting problem is to report better,
// not to start killing generations that are still working (ARC-30 cap 1).
const NO_PROGRESS_NOTICE_MS = NO_PROGRESS_NOTICE_SECONDS * 1000;
// /finish is a single point of failure guarding an expensive, already
// completed generation, so it gets bounded retries of its own.
const FINISH_ATTEMPTS = 3;
const FINISH_RETRY_BACKOFF_MS = [2000, 5000];
// Absolute ceiling on the whole finish() sequence, checked by the watchdog
// itself. Sits just above FINISH_ATTEMPTS * FINISH_TIMEOUT_MS plus the
// backoffs (277s), which means that with the constants as they stand today it
// is genuinely unreachable — requestJson's own abort always fires first. It is
// kept deliberately as defence in depth: it is what catches a future await
// added to finish() without a timeout of its own, which is exactly the mistake
// that produced this issue twice. Reviewers should expect no test to cover it,
// because no behaviour reaches it.
const FINISH_TOTAL_BUDGET_MS = 5 * 60 * 1000;
// The ONLY status we treat as "keep waiting". Everything else is terminal —
// the same rule server/blacksmith.ts:pollForCompletion() already applies
// (`while (lastResult.status === "processing" ...)`). The client used to
// recognise exactly two terminal values ("completed"/"failed") and poll
// straight through anything else until the watchdog tripped; that asymmetry
// between the two halves of our own codebase is ARC-26 cause D.
const IN_PROGRESS_STATUS = "processing";

// The analyzer is the only place answer options are ordered (Order Options
// Logically); Waggle never re-sorts a writer's or BCE's order. transformToQuestion()
// (server/blacksmith.ts) already builds `answers` directly from BCE's final
// SOCRATES-scored `options` order, so the order that arrives here is the order
// that displays — the SOCRATES feedback letters it names are aligned to it.
// The only transform left is capitalization cleanup (normalizeAnswerCapitalization),
// which runs exactly once.
//
// Keep BCE's raw retrieval order (topic-major, per-topic difficulty ramp —
// that's what BCE already submits tasks in) so the numbers Review/Finalize
// show match the BCE Benchmark Dashboard's numbering for the same session. A
// global difficulty-ramp sort by cognitive level is still available on-demand
// via FinalizeTest.tsx's "Sort by Difficulty" button.
function toFinalQuestions(generated: Question[]): Question[] {
  return generated.map(normalizeAnswerCapitalization);
}

// Random flavor lines shown while the user waits for Blacksmith. Cycled
// alongside the real progress bar/status below — added to, not replacing,
// this pre-existing pool (2026-07-06: restored after an earlier pass wrongly
// dropped it while adding live progress).
const STATIC_FLAIR = [
  "Warming up the question engine...",
  "Sharpening virtual pencils...",
  "Herding answer choices...",
  "Untangling tricky topics...",
  "Brewing fresh questions...",
  "Stretching the test muscles...",
  "Polishing the thinking caps...",
  "Rounding up key concepts...",
  "Giving distractors a pep talk...",
  "Charging assessment batteries...",
  "Asking the rubric nicely...",
  "Getting your assessment ready...",
  "Polishing distractors...",
  "Untangling the multiple choices...",
  "Untwisting a pretzel of logic...",
  "Whispering encouragement to the correct answers...",
  "Smoothing out the rough edges...",
  "Sharpening the red pen...",
  "Laminating something important...",
  "Straightening the answer key...",
  "Passing notes to the distractors...",
  "Lining up the test items single file...",
  "Calling roll for key skills...",
  "Tidying up the booklet...",
  "Putting a gold star on something deserving...",
];

// Flavor lines for the indeterminate final phase — every task has reported
// "completed" but the session is still processing (CDE duplicate-detection
// pass across the whole batch). BCE doesn't expose granular sub-status for
// this phase via blacksmith_get_status, so these are mixed into the flair
// pool once that phase starts instead of leaving a stalled-looking 100% bar
// with nothing happening.
const FINALIZING_FLAIR = [
  "Checking for duplicate questions...",
  "Comparing questions for overlap...",
  "Double-checking answer quality...",
  "Polishing the final details...",
  "Running one last quality pass...",
];

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// "2" -> "2nd", "3" -> "3rd", "11" -> "11th", "21" -> "21st", etc.
function ordinalSuffix(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const rem10 = n % 10;
  if (rem10 === 1) return `${n}st`;
  if (rem10 === 2) return `${n}nd`;
  if (rem10 === 3) return `${n}rd`;
  return `${n}th`;
}

type ProgressSnapshot = {
  phase: "submitting" | "generating" | "finalizing";
  completedTasks: number;
  totalTasks: number;
};

// Builds the full cycling pool: the original joke flair PLUS real-status
// lines that read live progress off a ref (so they always reflect the
// current n/m count, not whatever it was when the pool was built) PLUS the
// finalizing-phase lines once that phase is reached. Real-status entries are
// included twice so they show up roughly as often as the joke pool combined,
// without drowning out the personality entirely.
function buildFlairPool(totalItems: number, progressRef: { current: ProgressSnapshot }): Array<() => string> {
  const pool: Array<() => string> = STATIC_FLAIR.map((line) => () => line);

  const realStatus = () => {
    const p = progressRef.current;
    if (p.phase === "finalizing") {
      return FINALIZING_FLAIR[randInt(0, FINALIZING_FLAIR.length - 1)];
    }
    if (p.totalTasks > 0) {
      return `Generating question ${Math.min(p.completedTasks + 1, p.totalTasks)} of ${p.totalTasks}...`;
    }
    return "Generating your questions...";
  };
  pool.push(realStatus, realStatus);

  // Question-number variants. Clamp the upper bound so we don't ask for
  // question #14 when only 5 were requested.
  const upper = Math.max(1, totalItems);
  pool.push(() => `Negotiating with question #${randInt(1, upper)}...`);
  pool.push(() => `Looking for question #${randInt(1, upper)}, has anyone seen it...`);
  // Two distinct answer-choice letters.
  pool.push(() => {
    const letters = ["A", "B", "C", "D"];
    const a = letters[Math.floor(Math.random() * letters.length)];
    let b = letters[Math.floor(Math.random() * letters.length)];
    while (b === a) b = letters[Math.floor(Math.random() * letters.length)];
    return `Mediating a dispute between ${a} and ${b}...`;
  });
  // Counter-aware chalkboard erasure. First show is unsuffixed; each
  // subsequent show appends "for the Nth time...". Counter lives in the
  // closure so it's per-generation — a fresh pool starts at 1 again.
  let chalkboardCount = 0;
  pool.push(() => {
    chalkboardCount += 1;
    return chalkboardCount === 1
      ? "Erasing the chalkboard..."
      : `Erasing the chalkboard for the ${ordinalSuffix(chalkboardCount)} time...`;
  });
  return pool;
}

export default function GenerateTest() {
  const [, setLocation] = useLocation();
  const { blueprint, config, setQuestions, questions, testName, loadSavedState } = useTest();
  const { token } = useAuth();
  const { devMode } = useDevMode();

  // Dev fallback (Blacksmith down): drop a self-contained 10-question sample
  // into the review-and-revise step so people can try editing items.
  const loadDevSample = () => {
    const sample = buildSampleTestPayload();
    loadSavedState({
      config: sample.config,
      blueprint: sample.blueprint,
      questions: sample.questions,
      directions: sample.directions,
    });
    setLocation("/create/review-test");
  };
  const [error, setError] = useState<string | null>(null);
  // "timeout" reads as the user's situation ("your test isn't ready yet") and
  // keeps the recovery path prominent; "failed" is a genuine dead end. The
  // old copy put "Generation failed:" over the watchdog message, which reads
  // as terminal and hid a retry button that would often have recovered the
  // work (ARC-26 field report: "and that's it" — the user never clicked it).
  const [errorKind, setErrorKind] = useState<"timeout" | "failed">("failed");
  const [generationComplete, setGenerationComplete] = useState(false);
  // Drives the spinner. Previously the wheel rendered unconditionally, so it
  // spun during success, during failure, and after every timer had been
  // cleared — "the wheel is spinning" carried no information about whether
  // anything was still happening (ARC-26 cause A). "stopped" is the safety
  // net: any path that tears down the pollers without recording an outcome
  // lands here and says so, instead of spinning forever.
  type RunState = "running" | "stopped" | "error" | "complete";
  const [runState, setRunState] = useState<RunState>("running");
  // Consecutive-poll-failure state, surfaced in the UI rather than swallowed
  // into console.warn (ARC-26 cause C).
  const [pollTrouble, setPollTrouble] = useState<{ failures: number } | null>(null);
  // ARC-30 cap 1: what tests of this size have really taken here, so the page
  // can say "usually about 12 minutes" instead of leaving the user to guess
  // whether 12 minutes is normal or broken.
  const [typicalSeconds, setTypicalSeconds] = useState<number | null>(null);
  // Set when nothing has moved for a while. Purely informational — the run
  // continues.
  const [noProgressMinutes, setNoProgressMinutes] = useState<number | null>(null);
  // "Try Again" checks the existing session before restarting — brief UI
  // feedback while that check is in flight (usually well under a second).
  const [isCheckingExistingSession, setIsCheckingExistingSession] = useState(false);
  type GenerationPhase = "submitting" | "generating" | "finalizing";
  const [progress, setProgress] = useState<{
    phase: GenerationPhase;
    completedTasks: number;
    totalTasks: number;
  }>({ phase: "submitting", completedTasks: 0, totalTasks: 0 });
  const hasStartedGeneration = useRef(false);
  // True only while a Blacksmith request is actually in flight. Drives the
  // "leave anyway?" prompt — a refresh/back mid-generation abandons the
  // running job (it isn't cancelled server-side) and starts a brand-new one.
  const inFlightRef = useRef(false);
  // Poll/flair timers and run flags live in refs, not in the generation
  // effect's closure, so that whichever invocation of that effect React asks
  // to clean up can reach the timers the FIRST invocation actually created.
  // See the StrictMode note in the effect below (ARC-26).
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const flairIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const cancelledRef = useRef(false);
  // True from the moment finish() starts its first network call until it
  // either commits questions or fails. Stops a poll tick that lands mid-finish
  // from starting a second, concurrent finish() — the pollers now stay alive
  // across /finish (see finish() below).
  const finishingRef = useRef(false);
  // Monotonic counter identifying the current invocation of the generation
  // effect, used to tell a StrictMode remount from a real unmount.
  const effectRunRef = useRef(0);
  const lastFlairIndex = useRef(-1);
  // Persisted outside the run() effect's closure so the "Try Again" button
  // (rendered on error, well after run() has captured its own local
  // sessionId/generationSessionId) can still check on the SAME session
  // instead of blindly starting a new one. See "Try Again" button below —
  // a client-side watchdog timeout does not mean the server gave up; the
  // session may have finished in the background by the time the user clicks.
  const lastSessionIdRef = useRef<string | null>(null);
  const lastGenerationSessionIdRef = useRef<string | undefined>(undefined);

  // Warn before unloading (refresh / close / back-via-bfcache) while a
  // generation is in flight. Registered once; reads the live ref so the
  // closure never goes stale.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!inFlightRef.current) return;
      e.preventDefault();
      // Legacy requirement for the native confirmation dialog to show.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // How many items are being generated.
  const totalItems = blueprint
    ? blueprint.topics.reduce(
        (sum, t) => sum + t.knowledge + t.understanding + t.higherOrderThinking,
        0,
      )
    : config?.testLengthValue || 10;

  // Live progress, readable from inside flair-pool closures without
  // rebuilding the pool on every poll tick.
  const progressRef = useRef<ProgressSnapshot>({ phase: "submitting", completedTasks: 0, totalTasks: 0 });

  const flairPool = useRef(buildFlairPool(totalItems, progressRef));
  // Queue of upcoming indices into flairPool. Drains to empty, then we
  // reshuffle — guarantees every phrase appears before any repeats
  // (twelve-tone style).
  const flairQueue = useRef<number[]>([]);

  // Rebuild the pool when totalItems changes so the question-# flair stays
  // within range. Reset the queue too so the new pool gets a fresh cycle.
  useEffect(() => {
    flairPool.current = buildFlairPool(totalItems, progressRef);
    flairQueue.current = [];
  }, [totalItems]);

  // Fisher-Yates shuffle on indices.
  const reshuffle = (poolLength: number, avoidFirstIdx?: number): number[] => {
    const arr = Array.from({ length: poolLength }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    // If the freshly-shuffled deck would start on the phrase we just
    // showed (last item of the previous cycle), swap it with a later
    // element so back-to-back duplicates can't happen across cycles.
    if (avoidFirstIdx !== undefined && arr.length > 1 && arr[0] === avoidFirstIdx) {
      const swapWith = 1 + Math.floor(Math.random() * (arr.length - 1));
      [arr[0], arr[swapWith]] = [arr[swapWith], arr[0]];
    }
    return arr;
  };

  const pickFlair = () => {
    const pool = flairPool.current;
    if (flairQueue.current.length === 0) {
      const prev = lastFlairIndex.current >= 0 ? lastFlairIndex.current : undefined;
      flairQueue.current = reshuffle(pool.length, prev);
    }
    const idx = flairQueue.current.shift() as number;
    lastFlairIndex.current = idx;
    return pool[idx]();
  };

  const [flair, setFlair] = useState<string>(() => {
    const pool = buildFlairPool(0, progressRef);
    return pool[Math.floor(Math.random() * pool.length)]();
  });

  // Commits a retrieved batch and hands off to the navigation effect below.
  // Returns false when the batch is empty — that used to be its own silent
  // hang: setGenerationComplete(true) with questions.length === 0 leaves the
  // navigation effect waiting forever on a page whose timers are already
  // cleared. Callers must surface an error instead.
  const commitQuestions = (generated: Question[]): boolean => {
    const finalQuestions = toFinalQuestions(generated);
    if (finalQuestions.length === 0) return false;
    setQuestions(finalQuestions);
    setGenerationComplete(true);
    setRunState("complete");
    return true;
  };

  // Navigation effect: only fires once React has committed both setQuestions
  // and setGenerationComplete, guaranteeing questions.length > 0 when
  // ReviewTest mounts and preventing it from bouncing back to /create/generate.
  useEffect(() => {
    if (generationComplete && questions.length > 0) {
      setLocation("/create/review-test");
    }
  }, [generationComplete, questions.length, setLocation]);

  useEffect(() => {
    if (!blueprint) {
      setLocation("/create/blueprint");
      return;
    }

    // If questions already exist, skip directly to review
    if (questions && questions.length > 0) {
      setLocation("/create/review-test");
      return;
    }

    // ARC-26: React 18 StrictMode mounts, unmounts and remounts every effect
    // in development. The double-generation guard below means run() is only
    // ever STARTED on the first invocation, so the simulated unmount's
    // teardown used to cancel the one and only run: `cancelled` went true,
    // the second invocation bailed out on the ref guard, and the page sat on
    // a spinner with nothing polling behind it — forever, on every single dev
    // run, having already submitted the job to BCE. (Verified against
    // origin/dev: /start fires, then zero status polls.) Production builds
    // don't double-invoke effects, so users never saw this one — but it is
    // still a wheel spinning with no poller, which is exactly what this issue
    // says must not exist.
    //
    // Fix, in two independently sufficient halves. The load-bearing one is
    // the `cancelledRef.current = false` below: re-entering the effect
    // un-cancels the run, so even if the teardown fires the second invocation
    // revives it. The effect versioning + deferred teardown is the other
    // half — a StrictMode remount re-enters synchronously and bumps the
    // version, so the deferred teardown sees a newer version and stands down,
    // while a real unmount never re-enters and still tears down. Both are kept
    // because relying on the revive alone leaves a window where the pollers
    // are stopped, and relying on the defer alone breaks if React ever stops
    // remounting synchronously. Everything the teardown touches lives in refs
    // rather than one invocation's closure, so whichever invocation React
    // asks to clean up can reach the real timers.
    const thisEffectRun = ++effectRunRef.current;
    cancelledRef.current = false;

    const stopAll = () => {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (flairIntervalRef.current) clearInterval(flairIntervalRef.current);
      statusIntervalRef.current = undefined;
      flairIntervalRef.current = undefined;
      inFlightRef.current = false;
      // The spinner is gated on runState, so tearing down the pollers without
      // recording an outcome now shows an explicit "stopped, outcome unknown"
      // panel rather than a wheel that cannot stop. fail()/commitQuestions()
      // overwrite this in the same tick; only a path that forgets to leaves
      // it visible, which is exactly the point (ARC-26 cause A).
      if (!cancelledRef.current) setRunState("stopped");
    };
    const applyProgress = (next: ProgressSnapshot) => {
      progressRef.current = next;
      setProgress(next);
    };

    const run = async () => {
      inFlightRef.current = true;
      try {
        console.log("Starting Blacksmith question generation...");
        const { sessionId, totalTasks, generationSessionId, estimate } = await startGeneration(
          blueprint, config || undefined, testName, token
        );
        if (cancelledRef.current) return;
        lastSessionIdRef.current = sessionId;
        lastGenerationSessionIdRef.current = generationSessionId;
        applyProgress({ phase: "generating", completedTasks: 0, totalTasks: totalTasks || totalItems });

        // Start the flair cycle immediately — don't wait for the finalizing
        // phase. A single question can take 25-50s to generate, and with no
        // cycling text in that window the page looked "stuck" even though
        // the n/m count was accurate (2026-07-06 feedback).
        setFlair(pickFlair());
        flairIntervalRef.current = setInterval(() => setFlair(pickFlair()), 3000);

        // Client-side watchdog so a genuinely stalled session eventually
        // surfaces a clear error instead of polling forever.
        //
        // ARC-30 cap 1. This WAS `300 + plannedTasks * 120` seconds, and it is
        // the real ceiling on batch generation — the server budget ARC-4
        // doubled sits on a path this flow stopped using on 2026-07-06, which
        // is why widening it changed nothing users could feel. The old formula
        // gave a 5-item test 15 minutes for everything including a finalizing
        // pass observed to take 10-15 minutes on its own.
        //
        // It is now derived, not picked:
        //   - floor: 120s submission overhead + 150s/item, from a measured
        //     49.5s/item (990s for 20 tasks, production 2026-07-05,
        //     corroborated by a healthy 10-question session still running at
        //     495s on 2026-07-20) times a safety multiple of 3;
        //   - refined by what THIS deployment has actually recorded for tests
        //     of this size, when it has at least two completed sessions to
        //     average — real durations beat any constant, and they can only
        //     ever argue for waiting LONGER (see refinedGeneratingCeilingSeconds).
        // The finalizing phase then gets its own budget on top, granted when
        // that phase is first seen.
        //
        // The direction of the trade has not changed and should not: erring
        // toward "wait longer" costs a user some minutes in front of an honest
        // progress bar; erring toward "false failure while the server keeps
        // working" throws away a finished generation and burns a duplicate.
        const plannedTasks = totalTasks || totalItems;
        const observedSeconds =
          estimate && estimate.estimateSeconds && estimate.sampleCount > 0
            ? estimate.estimateSeconds
            : null;
        if (observedSeconds) setTypicalSeconds(observedSeconds);
        const ceilingSeconds = refinedGeneratingCeilingSeconds(plannedTasks, observedSeconds);
        console.log(
          `[Generate] Watchdog budget: ${ceilingSeconds}s for ${plannedTasks} item(s) ` +
          `(${observedSeconds
            ? `refined by ${estimate?.sampleCount} recorded session(s) averaging ${Math.round(observedSeconds)}s`
            : "no recorded sessions of this size — using the observation-derived floor"})`
        );
        let deadline = Date.now() + ceilingSeconds * 1000;
        // Forward movement, for the "nothing has moved in N minutes" notice.
        // Starts now: submission counts as movement.
        let lastMovementAt = Date.now();
        let lastMovementKey = "";
        // The finalizing phase gets its own budget the first time we see it —
        // see FINALIZING_BUDGET_MS. Latched so a flapping phase can't keep
        // extending the deadline indefinitely.
        let finalizingBudgetApplied = false;
        let consecutivePollFailures = 0;
        // When we first saw a status that is neither "processing" nor a
        // recognised terminal value. null whenever the status is understood.
        let unknownStatusSince: number | null = null;
        // Guards against two ticks' worth of /status overlapping.
        let pollInFlight = false;
        // When the current finish() began, so the watchdog can bound it even
        // if the request never settles. null whenever finish() is not running.
        let finishStartedAt: number | null = null;
        // Latched once an outcome has been recorded, so a late-arriving
        // callback cannot overwrite it with a contradictory second error.
        let settled = false;

        const fail = (message: string, kind: "timeout" | "failed" = "failed") => {
          if (cancelledRef.current || settled) return;
          settled = true;
          stopAll();
          console.error("Question generation failed:", message);
          setPollTrouble(null);
          setNoProgressMinutes(null);
          setErrorKind(kind);
          setError(message);
          setRunState("error");
        };

        // ARC-26 cause B — the permanent, timeout-proof hang.
        //
        // The old finish() called stopAll() BEFORE the network call, so any
        // throw from finishGeneration() left the page with no pollers, no
        // error UI, no navigation and no watchdog (the deadline check lived
        // inside the interval that had just been cleared). The generation had
        // actually succeeded on BCE; the user got a permanently spinning
        // wheel with no recovery path at all, while the server's own /finish
        // error handler recorded the session as `failed`.
        //
        // Now: the pollers stay up until questions are actually in hand,
        // /finish gets bounded retries, and every exit sets an outcome.
        const finish = async (context?: string) => {
          if (finishingRef.current || cancelledRef.current || settled) return;
          finishingRef.current = true;
          finishStartedAt = Date.now();
          let lastErr: unknown;
          // Only the RETRIEVAL is inside the retry try/catch. Transforming and
          // committing happen after the loop, because a bug in the transform
          // (normalizeAnswerCapitalization throws on a malformed answer array) is
          // not a retrieval failure: retrying it would re-upload the whole blueprint
          // twice more and can duplicate the server-side problem inserts.
          let retrieved: Question[] | undefined;
          let retrievedOk = false;
          for (let attempt = 1; attempt <= FINISH_ATTEMPTS; attempt++) {
            try {
              retrieved = await finishGeneration(
                sessionId, blueprint, config || undefined, generationSessionId, token
              );
              retrievedOk = Array.isArray(retrieved);
              break;
            } catch (err) {
              if (cancelledRef.current) { finishingRef.current = false; finishStartedAt = null; return; }
              lastErr = err;
              console.warn(`[Generate] /finish attempt ${attempt}/${FINISH_ATTEMPTS} failed:`, err);
              const backoff = FINISH_RETRY_BACKOFF_MS[attempt - 1];
              if (attempt < FINISH_ATTEMPTS && backoff !== undefined) {
                await new Promise((resolve) => setTimeout(resolve, backoff));
              }
            }
          }
          finishingRef.current = false;
          finishStartedAt = null;
          if (cancelledRef.current) return;

          // Array.isArray, not `!== null`: a 200 whose body simply has no
          // `questions` key yields `undefined`, which passes a null check and
          // then throws on `.length`. That throw escaped finish() into the
          // poll tick's catch — where the consecutive-failure counter had
          // already been reset to 0 by the SUCCESSFUL /status call in the same
          // tick — so the cause-C cap could never fire and /finish was
          // re-POSTed every 3 seconds until the outer watchdog, up to 45
          // minutes on a 20-item test. Caught in review; finish() must be
          // total, and must never throw into its caller.
          if (retrievedOk) {
            try {
              console.log(`Generated ${retrieved!.length} questions via Blacksmith`);
              // The questions are in hand — only now is it safe to tear the
              // pollers down. Real questions win even if the watchdog already
              // gave up on this retrieval: navigating the user to the test they
              // actually have beats leaving them on an error.
              stopAll();
              if (commitQuestions(retrieved!)) {
                settled = true;
                setPollTrouble(null);
                setError(null);
                return;
              }
              // Retrieval succeeded but produced nothing usable. Retrying will
              // not conjure questions, so fail loudly rather than stranding
              // the navigation effect on an empty array.
              fail(
                `Your generation finished but came back with no questions (session: ${sessionId}). ` +
                `Nothing has been saved — please start over.`
              );
            } catch (err) {
              fail(
                `Your questions came back but could not be read (session: ${sessionId}): ` +
                `${err instanceof Error ? err.message : String(err)}`
              );
            }
            return;
          }

          // Retrieval "succeeded" but the payload was not a question array at
          // all. That is a malformed response, not a transport failure, and
          // retrying it would just re-POST the whole blueprint again.
          if (lastErr === undefined) {
            fail(
              `Your generation finished but the response could not be understood ` +
              `(session: ${sessionId}). Nothing has been saved — please start over.`
            );
            return;
          }

          const detail = lastErr instanceof Error ? lastErr.message : String(lastErr);
          fail(
            `${context ? `${context} ` : ""}Your questions were generated but could not be retrieved after ` +
            `${FINISH_ATTEMPTS} attempts: ${detail} (session: ${sessionId})`
          );
        };

        statusIntervalRef.current = setInterval(async () => {
          if (cancelledRef.current) return;

          // The watchdog is evaluated BEFORE any in-flight guard, and every
          // in-flight guard carries its own bound. An earlier revision of this
          // fix put `if (finishingRef.current) return;` first, which meant a
          // /finish that hung rather than throwing left the flag set forever,
          // every subsequent tick returned immediately, and the deadline was
          // never evaluated again — rebuilding the exact permanent hang this
          // issue is about. Guards that can suppress the watchdog must never
          // outlive a bound.
          if (finishingRef.current) {
            if (finishStartedAt !== null && Date.now() - finishStartedAt > FINISH_TOTAL_BUDGET_MS) {
              finishingRef.current = false;
              fail(
                `Your questions were generated but retrieving them stopped responding (session: ${sessionId}). ` +
                `Nothing has been lost — check again in a moment.`,
                "timeout",
              );
            }
            return;
          }
          if (Date.now() > deadline) {
            // Say how long we waited and, when we know it, what normal looks
            // like here. "Taking longer than expected" with no yardstick told
            // the user nothing they could act on — and the old copy's advice
            // ("try again in a bit") was the one action that actively hurt,
            // since starting over abandons a generation that is usually still
            // running (ARC-30 cap 1).
            const waited = describeDuration(ceilingSeconds);
            fail(
              `We waited ${waited} and your test still isn't ready (session: ${sessionId}). ` +
              (observedSeconds
                ? `Tests this size usually finish in ${describeDuration(observedSeconds)}. `
                : "") +
              `It has not failed — it is most likely still finishing, and nothing has been lost.`,
              "timeout",
            );
            return;
          }
          // A /status slower than the 3s poll interval would otherwise stack
          // ticks up: out-of-order resolutions reset the consecutive-failure
          // counter and clear the degraded banner, deferring the failure cap
          // indefinitely under exactly the conditions it exists to catch.
          // Bounded by STATUS_TIMEOUT_MS, so this cannot become a second way
          // to switch the watchdog off.
          if (pollInFlight) return;
          pollInFlight = true;
          try {
            const s = await checkGenerationStatus(sessionId, token);
            if (cancelledRef.current) return;
            consecutivePollFailures = 0;
            setPollTrouble(null);

            if (s.status === "completed") {
              await finish();
              return;
            }
            if (s.status === "failed") {
              // ARC-98 residual: a session BCE reports "failed" can still have
              // every task completed and persisted. Try to retrieve it before
              // giving up; /finish now accepts terminal-but-retrievable
              // sessions, so only a genuine retrieval failure surfaces here.
              try {
                await finish();
              } catch {
                fail(`${s.message} (session: ${sessionId})`);
              }
              return;
            }

            // ARC-26 cause D — recognise terminal states by exclusion, not by
            // an enumerated allow-list, and name the offending value.
            //
            // Anything that is not "processing" is terminal as far as
            // server/blacksmith.ts is concerned, so the client no longer
            // polls through it until the watchdog. It is tolerated for one
            // grace window first, because an unknown value could equally be
            // an early in-progress state we have never seen — killing every
            // healthy generation would be far worse than waiting a minute.
            // After that we try to retrieve anyway (the questions may well be
            // sitting there); if that fails, the error names the raw status,
            // which hands us the enum value we could not look up.
            if (s.status !== IN_PROGRESS_STATUS) {
              if (unknownStatusSince === null) {
                unknownStatusSince = Date.now();
                console.warn(
                  `[Generate] Unrecognised session status "${s.status}" — ` +
                  `tolerating for ${UNKNOWN_STATUS_GRACE_MS / 1000}s before treating it as terminal`
                );
              } else if (Date.now() - unknownStatusSince > UNKNOWN_STATUS_GRACE_MS) {
                await finish(`Generation reported an unrecognised status "${s.status}".`);
                return;
              }
            } else {
              unknownStatusSince = null;
            }

            const phase: GenerationPhase =
              s.completedTasks >= s.totalTasks && s.totalTasks > 0 ? "finalizing" : "generating";
            if (phase === "finalizing" && !finalizingBudgetApplied) {
              finalizingBudgetApplied = true;
              deadline = Math.max(deadline, Date.now() + FINALIZING_BUDGET_MS);
              console.log(
                `[Generate] Finalizing phase reached — granting it its own ` +
                `${FINALIZING_BUDGET_MS / 60000} minute budget`
              );
            }
            applyProgress({
              phase,
              completedTasks: s.completedTasks,
              totalTasks: s.totalTasks || plannedTasks,
            });

            // ARC-30 cap 1, the other half: a run that is inside its budget but
            // has not moved for several minutes used to look exactly like a
            // healthy one, so the user's only signal that anything was wrong
            // was the failure at the very end. Report the stall, keep waiting.
            // Deliberately NOT wired to fail(): the finalizing pass legitimately
            // reports no movement for 10-15 minutes, and killing those runs to
            // fix a reporting gap would re-create the exact bug this issue is
            // about, from the other direction.
            const movementKey = `${phase}:${s.completedTasks}/${s.totalTasks}`;
            if (movementKey !== lastMovementKey) {
              lastMovementKey = movementKey;
              lastMovementAt = Date.now();
              setNoProgressMinutes(null);
            } else if (Date.now() - lastMovementAt > NO_PROGRESS_NOTICE_MS) {
              setNoProgressMinutes(Math.floor((Date.now() - lastMovementAt) / 60000));
            }
          } catch (err) {
            if (cancelledRef.current) return;
            // ARC-26 cause C — not every poll failure is transient. The old
            // catch swallowed all of them into console.warn with no cap, no
            // counter and no distinction, so a permanently broken poll showed
            // a healthy spinner and cycling flair right up to the watchdog.
            consecutivePollFailures += 1;
            const status = err instanceof HttpError ? err.status : undefined;
            const detail = err instanceof Error ? err.message : String(err);

            // Note: authMiddleware answers 500 (not 401) when it cannot reach
            // the auth service, so a 401 here really is a rejected token and
            // is safe to treat as permanent.
            if (status === 401 || status === 403) {
              fail(`Your sign-in expired while your test was generating (session: ${sessionId}). Sign in again and check your dashboard — the test may still have completed.`);
              return;
            }
            if (status === 404) {
              fail(`That generation session is no longer available (session: ${sessionId}): ${detail}`);
              return;
            }
            if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
              fail(`Lost contact with the server after ${consecutivePollFailures} attempts: ${detail} (session: ${sessionId}). Your generation may still be running.`);
              return;
            }
            console.warn(
              `[Generate] Status poll failed (${consecutivePollFailures} in a row), will retry:`, err
            );
            if (consecutivePollFailures >= POLL_TROUBLE_AFTER) {
              setPollTrouble({ failures: consecutivePollFailures });
            }
          } finally {
            pollInFlight = false;
          }
        }, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        stopAll();
        console.error("Question generation failed:", err);
        setErrorKind("failed");
        setError(err instanceof Error ? err.message : "Failed to generate questions");
        setRunState("error");
      }
    };

    // Prevent double generation in development mode. Only the first
    // invocation starts a run; later ones exist purely to re-register the
    // teardown above.
    if (!hasStartedGeneration.current) {
      hasStartedGeneration.current = true;
      run();
    }

    return () => {
      // Deferred by a tick so a StrictMode remount (which re-enters this
      // effect synchronously, bumping effectRunRef) can stand this teardown
      // down. A real unmount never re-enters, so it still tears down.
      setTimeout(() => {
        if (effectRunRef.current !== thisEffectRun) return;
        cancelledRef.current = true;
        stopAll();
      }, 0);
    };
  }, [blueprint, config, setLocation, setQuestions]);

  // Check-before-restart (2026-07-20, reworked for ARC-26): a client-side
  // watchdog timeout does not mean the server gave up — the previous session
  // may well have finished in the background by the time the user clicks this
  // (exactly what happened in a real live session: starting over would have
  // silently thrown away a completed 10-question generation and burned
  // another ~8 minutes generating a duplicate).
  //
  // Two ARC-26 changes: the terminal check is inverted to match the poll loop
  // (anything that is not "processing" is worth trying to retrieve, not just
  // "completed"), and a session that really IS still processing no longer
  // falls through to a blind page reload — that reload abandoned a running
  // generation and started a duplicate. It now reports live progress and
  // leaves the user to check again, with "Start over" as a separate,
  // deliberate choice.
  const handleCheckAgain = async () => {
    const sid = lastSessionIdRef.current;
    if (!sid || !blueprint) {
      handleStartOver();
      return;
    }
    setIsCheckingExistingSession(true);
    try {
      const s = await checkGenerationStatus(sid, token);
      if (s.status === IN_PROGRESS_STATUS) {
        setErrorKind("timeout");
        setRunState("error");
        setError(
          s.totalTasks > 0
            ? `Still working — ${Math.min(s.completedTasks, s.totalTasks)} of ${s.totalTasks} questions done (session: ${sid}). Nothing has been lost; check again in a few minutes.`
            : `Still working on your test (session: ${sid}). Nothing has been lost; check again in a few minutes.`
        );
        return;
      }
      const generatedQuestions = await finishGeneration(
        sid, blueprint, config || undefined, lastGenerationSessionIdRef.current, token
      );
      if (commitQuestions(generatedQuestions)) {
        setError(null);
        return;
      }
      setErrorKind("failed");
      setRunState("error");
      setError(`That generation came back with no questions (session: ${sid}). Starting over is the only way forward.`);
    } catch (err) {
      console.warn("[Generate] Re-check failed:", err);
      setErrorKind("failed");
      setRunState("error");
      setError(
        `We couldn't reach your generation (session: ${sid}): ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsCheckingExistingSession(false);
    }
  };

  function handleStartOver() {
    setError(null);
    hasStartedGeneration.current = false;
    window.location.reload();
  }

  // Recovery actions, shared by the error panel and the "stopped, outcome
  // unknown" panel — both need the same three ways out.
  const recoveryActions = (
    <div className="flex flex-wrap gap-3 justify-center">
      <Button
        disabled={isCheckingExistingSession}
        onClick={handleCheckAgain}
        data-testid="button-retry-generation"
      >
        {isCheckingExistingSession ? (
          <>Checking... <Loader2 className="w-4 h-4 animate-spin" /></>
        ) : (
          <>Check for my test <RefreshCw className="w-4 h-4" /></>
        )}
      </Button>
      <Button
        variant="outline"
        disabled={isCheckingExistingSession}
        onClick={handleStartOver}
        data-testid="button-start-over"
      >
        Start over
      </Button>
      <Button
        variant="ghost"
        onClick={() => setLocation("/create/blueprint")}
        data-testid="button-back-to-blueprint"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Blueprint
      </Button>
    </div>
  );

  const devSampleAction = devMode ? (
    <div className="pt-2">
      <Button
        variant="secondary"
        onClick={loadDevSample}
        data-testid="button-load-sample-test"
      >
        Load sample test (dev)
      </Button>
      <p className="text-xs text-muted-foreground mt-1">
        Loads a 10-question sample into review &amp; revise items.
      </p>
    </div>
  ) : null;

  const heading =
    runState === "error"
      ? errorKind === "timeout"
        ? "Your test isn't ready yet."
        : "We couldn't finish your test."
      : runState === "stopped"
        ? "We've stopped waiting."
        : runState === "complete"
          ? "Your test is ready."
          : "Generating your test.";

  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Create Test" />

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        <ProgressIndicator currentStep={4} />

        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-2xl font-semibold text-foreground" data-testid="text-generation-heading">
              {heading}
            </h2>
          </div>

          {/* ARC-26 cause A: the wheel used to render unconditionally — during
              success, during failure, and after every timer had been cleared.
              It is now gated on real run state, and each non-running state has
              its own still, unambiguous indicator. */}
          {runState === "running" && (
            <div className="relative" data-testid="indicator-generating">
              <div className="absolute inset-0 animate-ping">
                <div className="w-24 h-24 rounded-full bg-primary/20" />
              </div>
              <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-primary">
                <Loader2 className="w-12 h-12 text-primary-foreground animate-spin" />
              </div>
            </div>
          )}
          {runState === "complete" && (
            <div
              className="flex items-center justify-center w-24 h-24 rounded-full bg-primary"
              data-testid="indicator-generation-complete"
            >
              <CheckCircle2 className="w-12 h-12 text-primary-foreground" />
            </div>
          )}
          {(runState === "error" || runState === "stopped") && (
            <div
              className="flex items-center justify-center w-24 h-24 rounded-full bg-muted"
              data-testid={runState === "error" ? "indicator-generation-error" : "indicator-generation-stopped"}
            >
              <AlertCircle className="w-12 h-12 text-muted-foreground" />
            </div>
          )}

          {/* "Stopped, outcome unknown" — reachable only if some path tears
              down the pollers without recording an outcome. Previously that
              produced an eternally spinning wheel with no way out; now it
              says so and offers the same recovery actions. */}
          {runState === "stopped" && (
            <div className="text-center space-y-4" data-testid="generation-stopped">
              <p className="text-foreground font-medium max-w-md" data-testid="text-generation-stopped">
                We stopped checking on your test and didn't get a clear answer either way. It may still
                have finished — checking is safe and won't start a new one.
              </p>
              {recoveryActions}
              {devSampleAction}
            </div>
          )}

          {runState === "running" && (
            <div className="w-full max-w-md space-y-3">
              <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden" data-testid="progress-bar">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                  style={{
                    width: `${
                      progress.totalTasks > 0
                        ? Math.min(100, Math.round((progress.completedTasks / progress.totalTasks) * 100))
                        : 5
                    }%`,
                  }}
                />
              </div>
              {/* Always-visible real count, separate from the cycling flair
                  below, so it's never ambiguous whether progress is actually
                  moving — a fixed line that only changes on real polls. */}
              {progress.totalTasks > 0 && (
                <p className="text-sm font-medium text-foreground text-center" data-testid="text-progress-count">
                  {progress.phase === "finalizing"
                    ? `All ${progress.totalTasks} questions generated — finishing up`
                    : `Question ${Math.min(progress.completedTasks + 1, progress.totalTasks)} of ${progress.totalTasks}`}
                </p>
              )}
              <p className="text-lg text-muted-foreground animate-pulse text-center" data-testid="text-progress">
                {flair}
              </p>
              {/* ARC-30 cap 1: what "normal" looks like on THIS deployment, from
                  recorded durations of tests this size. Without a yardstick the
                  user has no way to tell a slow-but-healthy run from a stuck
                  one, which is most of what "it timed out" reports turn out to
                  be. */}
              {typicalSeconds !== null && (
                <p className="text-sm text-muted-foreground text-center" data-testid="text-typical-duration">
                  Tests this size usually take {describeDuration(typicalSeconds)}.
                </p>
              )}
              {/* Still running, but nothing has moved for a while. Informational
                  by design — see NO_PROGRESS_NOTICE_MS. */}
              {noProgressMinutes !== null && (
                <p
                  className="text-sm font-medium text-center text-amber-600 dark:text-amber-500"
                  data-testid="text-no-progress"
                >
                  {progress.phase === "finalizing"
                    ? `Still on the final quality pass — no change for ${noProgressMinutes} minute${noProgressMinutes === 1 ? "" : "s"}. This pass runs across the whole test and can take 10-15 minutes; we're still waiting.`
                    : `No new questions for ${noProgressMinutes} minute${noProgressMinutes === 1 ? "" : "s"}. We're still waiting and haven't given up — nothing is lost if you leave this running.`}
                </p>
              )}
              {/* ARC-26 cause C: a degraded-but-retrying poll is now visible
                  well before the watchdog, instead of a healthy-looking
                  spinner over a silent console.warn. */}
              {pollTrouble && (
                <p
                  className="text-sm font-medium text-center text-amber-600 dark:text-amber-500"
                  data-testid="text-poll-trouble"
                >
                  Having trouble reaching the server — still retrying (attempt {pollTrouble.failures}).
                  Your test is most likely still being generated.
                </p>
              )}
            </div>
          )}
          {runState === "error" && (
            <div className="text-center space-y-4 mt-8" data-testid="generation-error">
              {/* The old copy read "Generation failed:" over the watchdog
                  message, which describes our internal state rather than the
                  user's — and made a recovery button that often WOULD have
                  worked look pointless. A timeout is now framed as what it is
                  to them: the test isn't ready yet, nothing is lost. */}
              <p
                className={`font-medium max-w-xl ${errorKind === "timeout" ? "text-foreground" : "text-destructive"}`}
                data-testid="text-error"
              >
                {error}
              </p>
              {errorKind === "timeout" && (
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Checking is safe — it looks for the test you already started and won't begin a new one.
                </p>
              )}
              {recoveryActions}
              {devSampleAction}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
